import { publicFetch } from '../services/publicFetch';

/**
 * Public-side OneMap location search. Mirrors the agent's
 * `super-picker/utils/onemapLocationSearch.ts` but routes through the
 * `super-picker-public` proxy so share-link recipients (who have no agent
 * signature) can still resolve a Reference Location for the Project
 * Research radius filter.
 */

export interface PublicOneMapSearchResult {
  SEARCHVAL: string;
  BLK_NO?: string;
  ROAD_NAME?: string;
  BUILDING?: string;
  ADDRESS?: string;
  POSTAL?: string;
  LATITUDE?: string;
  LONGITUDE?: string;
}

/**
 * Compose a friendly label from the OneMap fields. Same precedence as the
 * agent helper (`BUILDING + BLK_NO + ROAD_NAME (POSTAL)`), with sensible
 * fallbacks when individual parts are missing or marked `"NIL"`.
 */
export function formatOnemapLocationLabel(result: PublicOneMapSearchResult): string {
  const parts = [
    result.BUILDING && result.BUILDING !== 'NIL' ? result.BUILDING : '',
    result.BLK_NO,
    result.ROAD_NAME,
  ].filter(Boolean);
  const head = parts.join(' ').trim();
  const tail = result.POSTAL && result.POSTAL !== 'NIL' ? ` (${result.POSTAL})` : '';
  return (head || result.SEARCHVAL || result.ADDRESS || '').trim() + tail;
}

/** Pull lat/lng off a OneMap result, or `null` if the strings don't parse. */
export function parseOnemapCoordinates(
  result: PublicOneMapSearchResult,
): { lat: number; lng: number } | null {
  const lat = Number(result.LATITUDE);
  const lng = Number(result.LONGITUDE);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/**
 * Search OneMap via the public-router proxy. Returns up to 10 results per
 * page (OneMap's default). Empty / whitespace-only queries short-circuit
 * to `[]` so we don't hammer the proxy on every keystroke.
 */
export async function searchPublicOnemapLocations(
  query: string,
  pageNum = 1,
): Promise<PublicOneMapSearchResult[]> {
  const q = query.trim();
  if (!q) return [];
  const params = new URLSearchParams({ searchVal: q, pageNum: String(pageNum) });
  const res = await publicFetch(`/api/super-picker-public/onemap-search?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`OneMap search failed (${res.status})`);
  }
  const payload = (await res.json().catch(() => ({}))) as {
    results?: PublicOneMapSearchResult[];
  };
  return Array.isArray(payload.results) ? payload.results : [];
}

/**
 * Haversine distance between two WGS-84 points, in kilometres. Used to
 * compute the `Dist fr Ref. Loc` column for project research rows.
 */
export function haversineDistanceKm(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const R = 6371; // Earth's mean radius in km.
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h =
    sinLat * sinLat +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * sinLng * sinLng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
}
